/**
 * The launch chapter's screen guides and guided tours — ADR 9044.
 *
 * One entry per route of `./manifest.ts`, keyed by its `path` exactly as declared, in the
 * manifest's order. `tools/screen-guides.mjs` fails `census:features` on a route without an entry,
 * a key unwritten in any locale, or a tour anchor no element carries; `../guides.test.ts` checks
 * each anchor is carried by the screen its step belongs to.
 *
 * The texts (`locale/<locale>/launch.json`, `launch.guide.*`) were written from each screen's
 * code — what it reads, what each figure means, what each control writes, what each state says
 * and why — not from its comments. A screen change that changes what a planner sees changes its
 * guide in the same PR.
 *
 * The three pages below are disabled one by one in `./manifest.ts` (not the chapter, `docs/adr/9082`),
 * so the rail does not draw them and they are reached by a direct link; the guides describe the
 * screens as they behave, including the states they show while the platform publishes nothing for them.
 * The first five are the app «Asignación inicial» (`docs/adr/5001`): each has its five parts and its tour, and
 * none is exempt — the exemption `docs/adr/9082` gave the placeholder went with it (`docs/adr/9044` reserves
 * exemptions for a screen no planner reaches).
 */

import type { GuideTable } from '../guides';

export const LAUNCH_GUIDES: GuideTable = {
  'initial': {
    purposeKey: 'launch.guide.initial.articles.purpose',
    readingKey: 'launch.guide.initial.articles.reading',
    actionsKey: 'launch.guide.initial.articles.actions',
    statesKey: 'launch.guide.initial.articles.states',
    sourceKey: 'launch.guide.initial.articles.source',
    tour: [
      { anchor: 'initial-market', titleKey: 'launch.guide.initial.articles.tour.market.title', bodyKey: 'launch.guide.initial.articles.tour.market.body' },
      { anchor: 'headline', titleKey: 'launch.guide.initial.articles.tour.headline.title', bodyKey: 'launch.guide.initial.articles.tour.headline.body' },
      { anchor: 'kpi-row', titleKey: 'launch.guide.initial.articles.tour.cards.title', bodyKey: 'launch.guide.initial.articles.tour.cards.body' },
      { anchor: 'initial-articles-table', titleKey: 'launch.guide.initial.articles.tour.list.title', bodyKey: 'launch.guide.initial.articles.tour.list.body' },
      { anchor: 'initial-new-run', titleKey: 'launch.guide.initial.articles.tour.newRun.title', bodyKey: 'launch.guide.initial.articles.tour.newRun.body' },
    ],
  },
  'initial/simulate': {
    purposeKey: 'launch.guide.initial.simulate.purpose',
    readingKey: 'launch.guide.initial.simulate.reading',
    actionsKey: 'launch.guide.initial.simulate.actions',
    statesKey: 'launch.guide.initial.simulate.states',
    sourceKey: 'launch.guide.initial.simulate.source',
    tour: [
      { anchor: 'initial-simulate-market', titleKey: 'launch.guide.initial.simulate.tour.market.title', bodyKey: 'launch.guide.initial.simulate.tour.market.body' },
      { anchor: 'initial-simulate-form', titleKey: 'launch.guide.initial.simulate.tour.form.title', bodyKey: 'launch.guide.initial.simulate.tour.form.body' },
      { anchor: 'initial-simulate-compare', titleKey: 'launch.guide.initial.simulate.tour.compare.title', bodyKey: 'launch.guide.initial.simulate.tour.compare.body' },
      { anchor: 'initial-simulate-links', titleKey: 'launch.guide.initial.simulate.tour.links.title', bodyKey: 'launch.guide.initial.simulate.tour.links.body' },
    ],
  },
  'initial/runs': {
    purposeKey: 'launch.guide.initial.runs.purpose',
    readingKey: 'launch.guide.initial.runs.reading',
    actionsKey: 'launch.guide.initial.runs.actions',
    statesKey: 'launch.guide.initial.runs.states',
    sourceKey: 'launch.guide.initial.runs.source',
    tour: [
      { anchor: 'initial-run-picker', titleKey: 'launch.guide.initial.runs.tour.picker.title', bodyKey: 'launch.guide.initial.runs.tour.picker.body' },
      { anchor: 'headline', titleKey: 'launch.guide.initial.runs.tour.headline.title', bodyKey: 'launch.guide.initial.runs.tour.headline.body' },
      { anchor: 'kpi-row', titleKey: 'launch.guide.initial.runs.tour.cards.title', bodyKey: 'launch.guide.initial.runs.tour.cards.body' },
      { anchor: 'initial-run-articles', titleKey: 'launch.guide.initial.runs.tour.articles.title', bodyKey: 'launch.guide.initial.runs.tour.articles.body' },
      { anchor: 'initial-run-download', titleKey: 'launch.guide.initial.runs.tour.download.title', bodyKey: 'launch.guide.initial.runs.tour.download.body' },
      { anchor: 'initial-article-open', titleKey: 'launch.guide.initial.runs.tour.article.title', bodyKey: 'launch.guide.initial.runs.tour.article.body' },
      { anchor: 'initial-run-parameters', titleKey: 'launch.guide.initial.runs.tour.parameters.title', bodyKey: 'launch.guide.initial.runs.tour.parameters.body' },
    ],
  },
  'initial/lines': {
    purposeKey: 'launch.guide.initial.lines.purpose',
    readingKey: 'launch.guide.initial.lines.reading',
    actionsKey: 'launch.guide.initial.lines.actions',
    statesKey: 'launch.guide.initial.lines.states',
    sourceKey: 'launch.guide.initial.lines.source',
    tour: [
      { anchor: 'initial-run-picker', titleKey: 'launch.guide.initial.lines.tour.picker.title', bodyKey: 'launch.guide.initial.lines.tour.picker.body' },
      { anchor: 'why-list', titleKey: 'launch.guide.initial.lines.tour.list.title', bodyKey: 'launch.guide.initial.lines.tour.list.body' },
      { anchor: 'why-drivers', titleKey: 'launch.guide.initial.lines.tour.drivers.title', bodyKey: 'launch.guide.initial.lines.tour.drivers.body' },
      { anchor: 'why-decide', titleKey: 'launch.guide.initial.lines.tour.decide.title', bodyKey: 'launch.guide.initial.lines.tour.decide.body' },
      { anchor: 'initial-lines-download', titleKey: 'launch.guide.initial.lines.tour.download.title', bodyKey: 'launch.guide.initial.lines.tour.download.body' },
    ],
  },
  'initial/approvals': {
    purposeKey: 'launch.guide.initial.approvals.purpose',
    readingKey: 'launch.guide.initial.approvals.reading',
    actionsKey: 'launch.guide.initial.approvals.actions',
    statesKey: 'launch.guide.initial.approvals.states',
    sourceKey: 'launch.guide.initial.approvals.source',
    tour: [
      { anchor: 'initial-run-picker', titleKey: 'launch.guide.initial.approvals.tour.picker.title', bodyKey: 'launch.guide.initial.approvals.tour.picker.body' },
      { anchor: 'approvals-table', titleKey: 'launch.guide.initial.approvals.tour.queue.title', bodyKey: 'launch.guide.initial.approvals.tour.queue.body' },
      { anchor: 'approval-detail', titleKey: 'launch.guide.initial.approvals.tour.detail.title', bodyKey: 'launch.guide.initial.approvals.tour.detail.body' },
      { anchor: 'initial-approvals-download', titleKey: 'launch.guide.initial.approvals.tour.download.title', bodyKey: 'launch.guide.initial.approvals.tour.download.body' },
    ],
  },
  'plan': {
    purposeKey: 'launch.guide.plan.purpose',
    readingKey: 'launch.guide.plan.reading',
    actionsKey: 'launch.guide.plan.actions',
    statesKey: 'launch.guide.plan.states',
    sourceKey: 'launch.guide.plan.source',
    tour: [
      { anchor: 'page-status', titleKey: 'launch.guide.plan.tour.state.title', bodyKey: 'launch.guide.plan.tour.state.body' },
      { anchor: 'launch-option-picker', titleKey: 'launch.guide.plan.tour.picker.title', bodyKey: 'launch.guide.plan.tour.picker.body' },
      { anchor: 'launch-run', titleKey: 'launch.guide.plan.tour.run.title', bodyKey: 'launch.guide.plan.tour.run.body' },
      { anchor: 'launch-plan-reconciliation', titleKey: 'launch.guide.plan.tour.reconciliation.title', bodyKey: 'launch.guide.plan.tour.reconciliation.body' },
      { anchor: 'launch-plan-queue', titleKey: 'launch.guide.plan.tour.queue.title', bodyKey: 'launch.guide.plan.tour.queue.body' },
      { anchor: 'launch-plan-approve', titleKey: 'launch.guide.plan.tour.approve.title', bodyKey: 'launch.guide.plan.tour.approve.body' },
      { anchor: 'launch-plan-lines', titleKey: 'launch.guide.plan.tour.lines.title', bodyKey: 'launch.guide.plan.tour.lines.body' },
    ],
  },
  'size-runs': {
    purposeKey: 'launch.guide.sizeRuns.purpose',
    readingKey: 'launch.guide.sizeRuns.reading',
    actionsKey: 'launch.guide.sizeRuns.actions',
    statesKey: 'launch.guide.sizeRuns.states',
    sourceKey: 'launch.guide.sizeRuns.source',
    tour: [
      { anchor: 'page-status', titleKey: 'launch.guide.sizeRuns.tour.state.title', bodyKey: 'launch.guide.sizeRuns.tour.state.body' },
      { anchor: 'launch-option-picker', titleKey: 'launch.guide.sizeRuns.tour.picker.title', bodyKey: 'launch.guide.sizeRuns.tour.picker.body' },
      { anchor: 'launch-run', titleKey: 'launch.guide.sizeRuns.tour.run.title', bodyKey: 'launch.guide.sizeRuns.tour.run.body' },
      { anchor: 'launch-size-runs-gate', titleKey: 'launch.guide.sizeRuns.tour.gate.title', bodyKey: 'launch.guide.sizeRuns.tour.gate.body' },
      { anchor: 'launch-size-runs-doors', titleKey: 'launch.guide.sizeRuns.tour.doors.title', bodyKey: 'launch.guide.sizeRuns.tour.doors.body' },
      { anchor: 'launch-size-runs-control', titleKey: 'launch.guide.sizeRuns.tour.control.title', bodyKey: 'launch.guide.sizeRuns.tour.control.body' },
    ],
  },
  'withholding': {
    purposeKey: 'launch.guide.withholding.purpose',
    readingKey: 'launch.guide.withholding.reading',
    actionsKey: 'launch.guide.withholding.actions',
    statesKey: 'launch.guide.withholding.states',
    sourceKey: 'launch.guide.withholding.source',
    tour: [
      { anchor: 'page-status', titleKey: 'launch.guide.withholding.tour.state.title', bodyKey: 'launch.guide.withholding.tour.state.body' },
      { anchor: 'launch-option-picker', titleKey: 'launch.guide.withholding.tour.picker.title', bodyKey: 'launch.guide.withholding.tour.picker.body' },
      { anchor: 'launch-run', titleKey: 'launch.guide.withholding.tour.run.title', bodyKey: 'launch.guide.withholding.tour.run.body' },
      { anchor: 'launch-withholding-realised', titleKey: 'launch.guide.withholding.tour.realised.title', bodyKey: 'launch.guide.withholding.tour.realised.body' },
      { anchor: 'launch-withholding-figures', titleKey: 'launch.guide.withholding.tour.fraction.title', bodyKey: 'launch.guide.withholding.tour.fraction.body' },
    ],
  },
};
